import { useMemo, useState, useEffect } from 'react'
import * as actions from '@actions'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import { toast } from 'react-toastify'
import ModalHeader from '@/components/shared/CustomModal/ModalHeader'
import ModalBody from '@/components/shared/CustomModal/ModalBody'
import ModalFooter from '@/components/shared/CustomModal/ModalFooter'
import {
    ORG_DOCUMENT_STATUS_MODEL_APPLIED,
    DOCUMENT_STATUS_SAVED,
    DOC_PROCESS_RUNSHEET_STATUSES,
    PURCHASE_TYPE_PROCESS_DOCUMENT,
    ACCOUNT_TYPES,
} from '@/constants'
import usePayments from '@hook/usePayments'
import PaymentRequiredFlow from '@components/shared/Dialogs/PaymentRequiredDialog/PaymentRequiredFlow'
import { MdWarning } from 'react-icons/md'
import { PENDO_EVENTS, pendoTrack } from '@/utils/pendo'
import * as store from '@/store'
import { useWireValue } from '@forminator/react-wire'

const ProcessDocumentsModal = ({ 
    showState, 
    markedDocumentsState,
    runsheetId,
    runsheetDocs,
}) => {
    const [show, setShow] = showState
    const [markedDocuments, setMarkedDocuments] = markedDocumentsState
    const [docsWithAnnotations, setDocsWithAnnotations] = useState([])
    const [, setFundingSource] = useState(ACCOUNT_TYPES.Organization)
    const [hasInsufficientBalance, setHasInsufficientBalance] = useState(false)
    const [showPaymentInnerModal, setShowPaymentInnerModal] = useState(false)
    const payments = usePayments()
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const docsToProcess = useMemo(() => 
        markedDocuments
            .map(docId => runsheetDocs.find(r => r.document.id === docId))
            .map(r => ({ 
                id: r?.document.id, 
                orgStatus: r?.document.orgStatus, 
                reviewStatus: r?.document.reviewStatus, 
            }))
    , [markedDocuments, runsheetDocs])
    
    const docsToApplyModel = useMemo(() => 
        docsToProcess
            .filter(doc => 
                (doc.reviewStatus === DOCUMENT_STATUS_SAVED && !doc.orgStatus) 
                || DOC_PROCESS_RUNSHEET_STATUSES.includes(doc.orgStatus),
            )
            .map(d => d.id)
    , [docsToProcess])
    
    const docsToEnhance = useMemo(() => 
        docsToProcess
            .filter(doc => doc.orgStatus === ORG_DOCUMENT_STATUS_MODEL_APPLIED)
            .map(d => d.id)
    , [docsToProcess])
    
    const closeModal = () => {
        setShow(false)
    }
    
    const processDocument = async (docId, runsheetId) => {
        try {
            
            await actions.queueDocumentForProcessing(docId, runsheetId, docsToApplyModel.length, payments.fundingSource)
            actions.forgetHasPromptedToProcessDocumentId(docId)
            pendoTrack(PENDO_EVENTS.DOC_PROCESS, { docId })
            return true
        } catch (err) {
            // NOOP
            return false
        }
    }
    
    const handleProcessDocuments = async () => {
        const validDocs = docsToEnhance.length + docsToApplyModel.length
        
        setMarkedDocuments([])
        setDocsWithAnnotations([])
        
        if (validDocs > 0) {
            toast.success(`Processing started for ${validDocs} ${validDocs > 1 ? 'documents' : 'document'}.`)
            if (validDocs < docsToProcess.length)
                toast.warn('Some of the documents you have selected are still preprocessing.' +
                  ' Please wait for this to complete before processing.')
        } else {
            return toast.warn(`Selected ${markedDocuments.length > 1 ? 'documents are' : 'document is'}` +
              ' not ready to be Processed yet. Please try again when Preprocessing has finished.')
        }
        
        try {
            
            const processStatus = await Promise.all(docsToApplyModel.map(docId => processDocument(docId, runsheetId)))
            const failedDocs = processStatus.filter(ps => !ps)
            
            if (failedDocs.length) {
                toast.dismiss()
                toast.error(`Failed to process ${failedDocs.length}` +
                    ` ${failedDocs.length === 1 ? 'document' : 'documents'}`)
            }
            
            await Promise.all(docsToEnhance.map(docId => actions.handleRunsheetAnnotationProcess(runsheetId, docId)))
            
        } catch (err) {
            toast.dismiss()
            toast.error('Failed to process documents')
        }
        
        if (!remoteConfig?.realtimeEnabled) await actions.updateLocalCredits()
        await actions.fetchRunsheetById(runsheetId)
        
    }
    
    const handleMessage = docsLength => {
        if (!docsLength)
            return 'None documents'
        
        return `${docsLength.length} ${docsLength.length > 1 ? 'documents' : 'document'}`
    }
    
    useEffect(() => {
        setShowPaymentInnerModal(payments.shouldShowPurchaseDialog(
            PURCHASE_TYPE_PROCESS_DOCUMENT,
            docsToApplyModel.length,
        ))
    }, [docsToApplyModel])
    
    useEffect(() => {
        const fetchAnnotations = async () => {
            const annotations = await actions.hasUserAnnotations(docsToApplyModel)
            
            setDocsWithAnnotations(annotations)
        }
        
        if (docsToApplyModel.length && show)
            fetchAnnotations()
        
    }, [docsToApplyModel, show])
    
    return (
        <CustomModal
            open={show}
            modalHandler={closeModal}
            center>
            <ModalHeader>Process documents</ModalHeader>
            <ModalBody>
                <>
                    <p className="mb-1 text-lg">Documents processing will be locked from editing.</p>
                    
                    <ul className="list-disc ml-4">
                        {docsToApplyModel.length > 0 ?
                            <li> 
                                {`${handleMessage(docsToApplyModel)} will be processed`}
                            
                            </li> 
                            : null
                        }
                        
                        {docsToEnhance.length > 0 ?
                            <li> 
                                {`${handleMessage(docsToEnhance)} will be updated`}
                            
                            </li> 
                            : null
                        }
                    </ul>
                    
                    
                    {docsWithAnnotations.length > 0 ? 
                        <div className="collapse bg-base-100 mt-2 mb-2">
                            <input id="checkbox-collapse" type="checkbox" className="cursor-pointer" defaultChecked/>
                            <div className="collapse-title font-medium !cursor-default">
                                <label htmlFor="checkbox-collapse" className="cursor-pointer flex items-center">
                                    <MdWarning className="mr-2"/>
                                    Some documents have user annotations and these will be overwritten
                                </label>
                            </div>
                            <div className="collapse-content">
                                {docsWithAnnotations?.map(doc => (
                                    <p key={doc.id}>
                                        {doc.name}
                                    </p>
                                ))}
                            </div>
                        </div>
                        : null
                    }
                    
                    {showPaymentInnerModal && docsToApplyModel.length > 0 ? 
                        (
                            <>
                                <hr className="mt-4" />
                                <div className="PaymentRequiredDialog max-w-full mt-4">
                                    <PaymentRequiredFlow
                                        payments={payments}
                                        purchaseType={PURCHASE_TYPE_PROCESS_DOCUMENT}
                                        itemCount={docsToApplyModel?.length}
                                        onFundingSourceChange={setFundingSource}
                                        onInsufficientBalanceChange={setHasInsufficientBalance} />
                                </div>
                            </>
                        ) : null  
                    }
                </>
            </ModalBody>
            
            <ModalFooter>
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="mr-3 btn btn-primary btn-outline"
                        onClick={closeModal}>
                        Cancel
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => {
                            handleProcessDocuments()
                            closeModal()
                        }}
                        disabled={docsToApplyModel.length && hasInsufficientBalance}>
                        Process
                    </button>
                </div>
            </ModalFooter>
        </CustomModal>
    )
}

export default ProcessDocumentsModal
